import * as Sentry from "@sentry/react";
import type { Breadcrumb, Event } from "@sentry/react";
import { scrubSentryUrl } from "./sentry-scrub";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// #612 adversarial review, F1 — #612 put real filter text into the URL
// (learner emails on /participants, client/PL/staff names on
// /cohorts and /staff), and every event this SDK reports carries a URL:
// `event.request.url` on error/transaction events, and `breadcrumb.data.url`
// / `.to` / `.from` on the navigation and fetch/xhr breadcrumbs Replay
// records alongside. Scrubbing only `request.url` in `beforeSend` would
// leave every breadcrumb — including the ones a replay session shows — still
// carrying the raw value. See sentry-scrub.ts for the pure rewrite and its
// own tests.
function scrubUrlFields<T extends { url?: string; to?: string; from?: string }>(
  data: T | undefined,
): T | undefined {
  if (!data) return data;
  const next = { ...data };
  if (typeof next.url === "string") next.url = scrubSentryUrl(next.url) as T["url"];
  if (typeof next.to === "string") next.to = scrubSentryUrl(next.to) as T["to"];
  if (typeof next.from === "string") next.from = scrubSentryUrl(next.from) as T["from"];
  return next;
}

function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.data) {
    breadcrumb.data = scrubUrlFields(breadcrumb.data);
  }
  return breadcrumb;
}

// Generic over `E extends Event` (rather than fixed to the base `Event`
// type) so this one function assigns cleanly to both `beforeSend`
// (`ErrorEvent`) and `beforeSendTransaction` (`TransactionEvent`) below —
// each expects its own event subtype back, not the wider base type.
function scrubEvent<E extends Event>(event: E): E {
  if (event.request?.url) {
    event.request = { ...event.request, url: scrubSentryUrl(event.request.url) };
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(beforeBreadcrumb);
  }
  return event;
}

if (dsn) {
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_DEPLOYMENT_TIER as string | undefined) ?? "dev",
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeBreadcrumb,
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
  });
} else {
  console.warn("[sentry] VITE_SENTRY_DSN not set — Sentry disabled");
}

// Tag Sentry with the Clerk user ID only — no email/PII.
// Avenue is an internal tool, but Sentry replays and error reports may be
// retained beyond the user's session; keeping PII out of them is the default.
export function tagClerkUser(user: { id: string }) {
  Sentry.setUser({ id: user.id });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
